import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from '@infrastructure/http/HttpClient';
import { AppThunk } from '@root/store/store';
import { AccountChambersCount } from '@api-model/accountChambersCount';
import { ChamberFilterOptions } from '@api-model/Enums';
import { ChamberSearchParams } from 'models/chamberSearchParams';
import { convertSearchParamsToQueryString } from '@shared/utils/searchParamsConverter';
import { union } from 'lodash';

export interface SiteListFilterStats {
  totalFilteredChambers: number;
  currentPage: number;
  totalPages: number;
}

export interface SitesListState extends SiteListFilterStats {
  textFilter: string;
  optionFilter: ChamberFilterOptions;
  ids: number[];
  totalChambers: number;
  selectedAccountTotalChambers: number;
}

const slice = createSlice({
  name: 'sitesList',
  initialState: {
    ids: [],
    totalFilteredChambers: 0,
    currentPage: 1,
    totalPages: 1,
    totalChambers: 0,
    selectedAccountTotalChambers: 0,
    textFilter: '',
    optionFilter: ChamberFilterOptions.all,
  } as SitesListState,
  reducers: {
    resetVisibleIdsAndFilterStats(state: SitesListState) {
      state.ids = [];
      state.totalFilteredChambers = 0;
      state.currentPage = 1;
      state.totalPages = 1;
    },
    setVisibleIds(state: SitesListState, { payload }: PayloadAction<number[]>) {
      state.ids = payload;
    },
    appendVisibleIds(state: SitesListState, { payload }: PayloadAction<number[]>) {
      const next = union(state.ids, payload);
      state.ids = next;
    },
    setTotalFilteredChambers(state: SitesListState, { payload }: PayloadAction<number>) {
      state.totalFilteredChambers = payload;
    },
    setFilterStats(state: SitesListState, { payload }: PayloadAction<SiteListFilterStats>) {
      state.totalFilteredChambers = payload.totalFilteredChambers;
      state.currentPage = payload.currentPage;
      state.totalPages = payload.totalPages;
    },
    setTotalChambers(state: SitesListState, { payload }: PayloadAction<number>) {
      state.totalChambers = payload;
    },
    setSelectedAccountTotalChambers(state: SitesListState, { payload }: PayloadAction<number>) {
      state.selectedAccountTotalChambers = payload;
    },
    applyTextFilter(state: SitesListState, { payload }: PayloadAction<string>) {
      state.textFilter = payload;
    },
    applyOptionFilter(state: SitesListState, { payload }: PayloadAction<ChamberFilterOptions>) {
      state.optionFilter = payload;
    },
  },
});

const fetchChambersTotal = (searchParams: ChamberSearchParams): AppThunk => async (dispatch) => {
  const queryParams = convertSearchParamsToQueryString(searchParams);
  const { data } = await httpClient.get<AccountChambersCount>(`chambers/count?${queryParams}`);
  dispatch(slice.actions.setSelectedAccountTotalChambers(data.total));
  dispatch(slice.actions.setTotalFilteredChambers(data.filteredChambersCount));
};

const thunks = {
  fetchChambersTotal,
};

export const sitesListSlice = {
  reducer: slice.reducer,
  actions: slice.actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof slice.actions;
  thunks: typeof thunks;
};
